import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import BasicCreative from 'features/creative/add/basic/BasicCreative'
import CreativeAssets from 'features/creative/add/build/CreativeAssets'
import Thumbnail from 'features/creative/add/asset/Thumbnail'
import MultiThumbnail from 'features/creative/add/asset/MultiThumbnail'
import AdditionalTitle from 'features/creative/add/asset/AdditionalTitle'
import AdditionalLink from 'features/creative/add/asset/AdditionalLink'
import Subject from 'features/creative/add/asset/Subject'
import PriceTable from 'features/creative/add/asset/PriceTable'
import Calculate from 'features/creative/add/asset/Calculate'
import PhoneNumber from 'features/creative/add/asset/PhoneNumber'
import TalkChannel from 'features/creative/add/asset/TalkChannel'
import {
  addCreative,
  addImageLibrary,
  fetchCreativeGenerable,
  reset,
  saveAsCreative,
  setAssetChecked,
  setCreativeName,
} from 'features/creative/add/build/buildCreativeSlice'
import TextInput from 'components/form/TextInput'
import ErrorText from 'components/form/ErrorText'
import useCreativeValidation from 'features/creative/add/common/useCreativeValidation'
import { useAlert } from 'features/dialog/Alert'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import { useConfirm } from 'features/dialog/Confirm'
import Button from 'components/form/Button'
import Limitations from 'features/Limitations'
import { isPresent, noop } from 'utils'
import ImageLibraryModal from 'features/image/ImageLibraryModal'
import { ImageLibraryModalContext } from 'features/image/ImageLibraryModalContext'
import NoneAnchor from 'components/NoneAnchor'
import { hideImageLibraryModal } from 'features/image/imageLibrarySlice'
import { setThumbnailId } from 'features/creative/add/asset/thumbnailSlice'
import { setMultiThumbnailId } from 'features/creative/add/asset/multiThumbnailSlice'
import { BizChannelTypes } from '../../../Codes'
import ThumbnailRecommend from '../asset/ThumbnailRecommend'
import ThumbnailCustom from '../asset/ThumbnailCustom'
import TalkChannelLegalNotice from '../basic/TalkChannelLegalNotice'
import { checkLegalNotice } from '../../creativeUtil'
import {
  reset as resetBasicCreative,
  setLicenseReview,
} from '../basic/basicCreativeSlice'
import { setThumbnailRecommendId } from '../asset/thumbnailRecommendSlice'
import { setThumbnailCustomId } from '../asset/thumbnailCustomSlice'
import { isNil } from 'lodash'

const maxCount = Limitations.BIZ_CHANNEL_MAX_CREATIVES
const BuildCreative = ({
  readOnly = false,
  afterSave,
  onClickOtherTab,
  otherTabName = '기존 소재 불러오기',
  saveAs = false,
  adGroupId,
  imported = false,
  bizChannelType,
}) => {
  const dispatch = useDispatch()
  const {
    buildCreative: { creativeName, activeAssets, isLoading, channel },
    basicCreative: { licenseReview },
    assets: { thumbnail, multiThumbnail, thumbnailRecommend, thumbnailCustom },
  } = useSelector(state => state.creative)
  const afterConfirmRef = useRef(noop)
  const { errors, validate, clearError } = useCreativeValidation()
  const [ValidationAlert, showValidationAlert] = useAlert()
  const [CancelCreativeAddConfirm, showCancelCreativeAddConfirm] = useConfirm()
  const type = useMemo(
    () =>
      bizChannelType ||
      (channel ? channel.bizChannelType : BizChannelTypes.WEBSITE_BIZ_CHANNEL),
    [bizChannelType, channel]
  )

  useEffect(
    () => () => {
      batch(() => {
        dispatch(reset())
        dispatch(resetBasicCreative())
        dispatch(hideImageLibraryModal())
      })
    },
    [dispatch]
  )
  useEffect(() => clearError(), [bizChannelType, channel, clearError])
  useEffect(() => {
    if (type === BizChannelTypes.TALK_CHANNEL) {
      dispatch(setLicenseReview(checkLegalNotice(channel)))
    }
  }, [dispatch, channel, type])

  const handleSubmit = async () => {
    const { error, payload } = saveAs
      ? await dispatch(saveAsCreative(adGroupId))
      : await dispatch(addCreative())
    if (error) {
      failWithErrorResponse(payload)
    } else {
      success('소재가 저장되었습니다.')
      afterSave(true)
    }
  }
  const getAssetImages = () => {
    const images = []
    if (activeAssets.thumbnail && !isPresent(thumbnail.id)) {
      images.push({
        name: thumbnail.name,
        url: thumbnail.url,
        width: thumbnail.w,
        height: thumbnail.h,
        size: thumbnail.size,
      })
    }
    if (activeAssets.multiThumbnail) {
      multiThumbnail.images.forEach(item => {
        if (!isPresent(item.id)) {
          images.push({
            name: item.name,
            url: item.url,
            width: item.w,
            height: item.h,
            size: item.size,
          })
        }
      })
    }
    if (type === BizChannelTypes.TALK_CHANNEL) {
      if (isNil(thumbnailRecommend?.thumbnail?.id)) {
        images.push({
          name: thumbnailRecommend.thumbnail.name,
          url: thumbnailRecommend.thumbnail.url,
          width: thumbnailRecommend.thumbnail.width,
          height: thumbnailRecommend.thumbnail.height,
          size: thumbnailRecommend.thumbnail.size,
        })
      }
      if (isNil(thumbnailCustom?.thumbnail?.id)) {
        images.push({
          name: thumbnailCustom.thumbnail.name,
          url: thumbnailCustom.thumbnail.url,
          width: thumbnailCustom.thumbnail.width,
          height: thumbnailCustom.thumbnail.height,
          size: thumbnailCustom.thumbnail.size,
        })
      }
    }
    return images
  }
  const handleCreativeSave = async () => {
    const {
      payload: { generable },
    } = await dispatch(fetchCreativeGenerable())
    if (!generable) {
      showValidationAlert()
      return
    }
    try {
      await validate()
    } catch (e) {
      if (!e.anyError) {
        const assetImages = getAssetImages()
        if (assetImages.length > 0) {
          const { error: imageError, payload: imagePayload } = await dispatch(
            addImageLibrary(assetImages)
          )
          if (imageError) {
            const { errorCode, errorMessage } = imagePayload
            fail(
              errorCode === 112002
                ? `계정당 추가할 수 있는 이미지는 ${Limitations.MAX_IMAGE_LIBRARIES}개입니다.`
                : errorMessage
            )
          } else {
            batch(() => {
              dispatch(setThumbnailId(imagePayload))
              dispatch(setMultiThumbnailId(imagePayload))
              dispatch(setThumbnailRecommendId(imagePayload))
              dispatch(setThumbnailCustomId(imagePayload))
            })
            await handleSubmit()
          }
        } else {
          await handleSubmit()
        }
      }
    }
  }

  return (
    <ImageLibraryModalContext.Provider
      value={{
        afterConfirmRef,
        type,
      }}>
      <div className="material_detail">
        <div className="top_material">
          <div className="tit_material">
            <h3 className="subtit_subject">소재 등록</h3>
            {readOnly === false && saveAs === false && (
              <ul className="tap_complete">
                <li className="on">
                  <NoneAnchor className="btn_complete">
                    <span className="inner_g">
                      <span className="ico_comm" />새 소재
                    </span>
                  </NoneAnchor>
                </li>
                {onClickOtherTab && (
                  <li>
                    <NoneAnchor
                      className="btn_complete"
                      onClick={onClickOtherTab}>
                      <span className="inner_g">
                        <span className="ico_comm" />
                        {otherTabName}
                      </span>
                    </NoneAnchor>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
        {type === BizChannelTypes.TALK_CHANNEL ? (
          <>
            <ThumbnailRecommend
              disabled={readOnly || channel === null}
              errors={errors?.thumbnailRecommend}
            />
            <ThumbnailCustom
              disabled={readOnly || channel === null}
              errors={errors?.thumbnailCustom}
            />
            {licenseReview && (
              <TalkChannelLegalNotice
                disabled={readOnly}
                errors={errors?.basicCreative}
              />
            )}
          </>
        ) : (
          <>
            <BasicCreative
              disabled={readOnly || channel === null}
              errors={errors?.basicCreative}
            />
            <CreativeAssets
              assetNums={Object.values(activeAssets).filter(Boolean).length}
              onConfirmAssetAdd={v => dispatch(setAssetChecked(v))}
              disabled={readOnly || !channel}>
              {activeAssets.thumbnail && (
                <Thumbnail disabled={readOnly} errors={errors?.thumbnail} />
              )}
              {activeAssets.multiThumbnail && (
                <MultiThumbnail
                  disabled={readOnly}
                  errors={errors?.multiThumbnail}
                  imported={imported}
                />
              )}
              {activeAssets.additionalTitle && (
                <AdditionalTitle
                  disabled={readOnly}
                  errors={errors?.additionalTitle}
                />
              )}
              {activeAssets.additionalLink && (
                <AdditionalLink
                  disabled={readOnly}
                  errors={errors?.additionalLink}
                  imported={imported}
                />
              )}
              {activeAssets.subject && (
                <Subject
                  disabled={readOnly}
                  errors={errors?.subject}
                  imported={imported}
                />
              )}
              {activeAssets.priceTable && (
                <PriceTable
                  disabled={readOnly}
                  errors={errors?.priceTable}
                  imported={imported}
                />
              )}
              {activeAssets.calculate && (
                <Calculate
                  disabled={readOnly}
                  errors={errors?.calculate}
                  imported={imported}
                />
              )}
              {activeAssets.phoneNumber && (
                <PhoneNumber disabled={readOnly} errors={errors?.phoneNumber} />
              )}
              {activeAssets.talkChannel && (
                <TalkChannel disabled={readOnly} errors={errors?.talkChannel} />
              )}
            </CreativeAssets>
          </>
        )}
        <div className="add_material">
          <div className="material_item">
            <div className="tit_itemwrap">
              <strong className="tit_item">소재 이름</strong>
            </div>
            <div className="desc_item">
              <TextInput
                placeholder="소재 이름을 입력하세요"
                maxLength={50}
                value={creativeName}
                trim
                onValueChange={v => dispatch(setCreativeName(v))}
                disabled={readOnly}
                error={errors?.creativeName}
              />
              <ErrorText error={errors?.creativeName} />
            </div>
          </div>
        </div>
      </div>
      <div className="page_btn">
        <div className="inner_btn">
          {readOnly ? (
            <Button onClick={afterSave} primary>
              확인
            </Button>
          ) : (
            <>
              <Button onClick={showCancelCreativeAddConfirm}>취소</Button>
              <Button
                onClick={handleCreativeSave}
                disabled={!channel || isLoading}
                primary>
                저장
              </Button>
            </>
          )}
        </div>
      </div>
      <ValidationAlert confirmText="확인">
        <strong className="tit_layer">
          선택한 비즈채널 내 등록 가능한
          <br />
          소재 개수가 초과되었습니다.
        </strong>
        비즈채널에 등록할 수 있는 소재 개수는 최대{' '}
        {maxCount === 10000 ? '1만' : maxCount}개 입니다.
        <br />
        해당 비즈채널의 소재를 삭제하신 후에
        <br />
        등록 가능합니다.
      </ValidationAlert>
      <CancelCreativeAddConfirm
        title="소재등록 취소"
        onClose={({ isConfirmed }) => isConfirmed && afterSave(false)}>
        소재등록을 취소하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </CancelCreativeAddConfirm>
      <ImageLibraryModal afterConfirm={afterConfirmRef.current} />
    </ImageLibraryModalContext.Provider>
  )
}

export default BuildCreative

BuildCreative.propTypes = {
  bizChannelType: PropTypes.oneOf([
    BizChannelTypes.WEBSITE_BIZ_CHANNEL,
    BizChannelTypes.TALK_CHANNEL,
  ]),
  afterSave: PropTypes.func,
  onClickOtherTab: PropTypes.func,
  readOnly: PropTypes.bool,
  otherTabName: PropTypes.string,
  saveAs: PropTypes.bool,
  adGroupId: PropTypes.string,
  imported: PropTypes.bool,
}
